/**
 * CompareView — Upload a reference document and compare it against a generated report.
 * Renders inline below the report iframe when the user clicks "Compare".
 */
import { useRef, useState } from 'react'
import { reports } from '@/api/client'
import type { CompareResult, CompareDiscrepancy } from '@/types'

interface CompareViewProps {
  jobId:   string
  onClose: () => void
}

const ACCEPTED = '.pdf,.html,.htm,.docx,.txt'
const ACCEPT_LABEL = 'PDF, HTML, DOCX, or TXT'

// ── Icons ─────────────────────────────────────────────────────────────────────

function UploadIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 16V8M8 12l4-4 4 4M5 20h14" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor"
            strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}

// ── Sub-components ────────────────────────────────────────────────────────────

function DropZone({
  file, onFile,
}: { file: File | null; onFile: (f: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const f = e.dataTransfer.files[0]
    if (f) onFile(f)
  }

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`
        flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed
        cursor-pointer transition-colors select-none
        ${dragging
          ? 'border-violet-400 bg-violet-50'
          : file
            ? 'border-emerald-300 bg-emerald-50'
            : 'border-slate-200 bg-slate-50 hover:border-slate-300 hover:bg-slate-100'
        }
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        className="hidden"
        onChange={e => { if (e.target.files?.[0]) onFile(e.target.files[0]) }}
      />
      {file ? (
        <>
          <span className="text-2xl">📄</span>
          <p className="text-sm font-medium text-emerald-700">{file.name}</p>
          <p className="text-xs text-slate-400">Click to change file</p>
        </>
      ) : (
        <>
          <span className="text-slate-400"><UploadIcon /></span>
          <p className="text-sm font-medium text-slate-600">
            Drop your reference document here
          </p>
          <p className="text-xs text-slate-400">or click to browse · {ACCEPT_LABEL}</p>
        </>
      )}
    </div>
  )
}

function SectionList({ title, items, color }: {
  title: string; items: string[]; color: 'blue' | 'amber'
}) {
  if (!items.length) return null
  const cls = color === 'blue'
    ? 'bg-blue-50 border-blue-100 text-blue-800'
    : 'bg-amber-50 border-amber-100 text-amber-800'
  return (
    <div className={`rounded-xl border p-4 ${cls}`}>
      <h4 className="text-xs font-semibold uppercase tracking-wide mb-2 opacity-70">{title}</h4>
      <ul className="space-y-1">
        {items.map((item, i) => (
          <li key={i} className="text-sm flex gap-2">
            <span className="opacity-50 flex-shrink-0">•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function DiscrepancyCard({ d }: { d: CompareDiscrepancy }) {
  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-2">
      <p className="text-sm font-semibold text-slate-700">{d.topic}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
          <p className="text-xs font-semibold text-blue-600 mb-1">Uploaded says</p>
          <p className="text-xs text-blue-800">{d.uploaded_says}</p>
        </div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
          <p className="text-xs font-semibold text-slate-500 mb-1">Generated says</p>
          <p className="text-xs text-slate-700">{d.generated_says}</p>
        </div>
      </div>
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function CompareView({ jobId, onClose }: CompareViewProps) {
  const [file,    setFile]    = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  const [result,  setResult]  = useState<CompareResult | null>(null)

  const handleCompare = async () => {
    if (!file) return
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const res = await reports.compareReport(jobId, file)
      setResult(res)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Comparison failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-5 pb-16 animate-fade-up">
      <div className="bg-white rounded-2xl shadow-card border border-slate-100 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-semibold text-slate-800">Compare Reports</h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Upload a reference document and get an AI-powered comparison.
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400
                       hover:bg-slate-100 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Drop zone */}
          {!result && (
            <>
              <DropZone file={file} onFile={setFile} />

              {error && (
                <p className="text-sm text-red-500 text-center">{error}</p>
              )}

              <div className="flex justify-end">
                <button
                  onClick={handleCompare}
                  disabled={!file || loading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white
                             text-sm font-medium hover:bg-violet-700 transition-colors
                             disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white
                                       rounded-full animate-spin" />
                      Comparing…
                    </>
                  ) : (
                    'Compare'
                  )}
                </button>
              </div>
            </>
          )}

          {/* Result */}
          {result && (
            <div className="space-y-5">
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400">
                <span>📄 <strong className="text-slate-600">{result.source_filename}</strong></span>
                <span>vs</span>
                <span>📊 <strong className="text-slate-600">{result.report_title || 'Generated report'}</strong></span>
                <span className="ml-auto">⏱ {result.processing_time_s}s</span>
              </div>

              {/* Summary */}
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-4">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">
                  Summary
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed">{result.summary}</p>
              </div>

              {/* Topic coverage */}
              <div className="grid sm:grid-cols-2 gap-4">
                <SectionList
                  title="Only in uploaded"
                  items={result.in_uploaded_only}
                  color="blue"
                />
                <SectionList
                  title="Only in generated"
                  items={result.in_generated_only}
                  color="amber"
                />
              </div>

              {/* Discrepancies */}
              {result.discrepancies.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">
                    Discrepancies ({result.discrepancies.length})
                  </h4>
                  <div className="space-y-3">
                    {result.discrepancies.map((d, i) => (
                      <DiscrepancyCard key={i} d={d} />
                    ))}
                  </div>
                </div>
              )}

              {/* Verdict */}
              {result.verdict && (
                <div className="rounded-xl border border-violet-100 bg-violet-50 p-4">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-violet-500 mb-2">
                    Verdict
                  </h4>
                  <p className="text-sm text-violet-900 leading-relaxed">{result.verdict}</p>
                </div>
              )}

              {/* Compare again */}
              <div className="flex justify-end">
                <button
                  onClick={() => { setResult(null); setFile(null) }}
                  className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline"
                >
                  Compare a different document
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
